import React, { useState, useEffect } from 'react';
import { getClients, getDriver, createDailyEntry, getDriverProfile, getClient } from '../api/api';
import { format } from 'date-fns';

function DailyEntryForm({ user }) {
  const [driver, setDriver] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [eventCodes, setEventCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    client_id: '',
    events: {},
    comments: ''
  });

  const [calculated, setCalculated] = useState({
    total_load: 0,
    total_processed: 0,
    total_revenue: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.client_id) {
      const client = clients.find(c => c.id === parseInt(formData.client_id));
      setSelectedClient(client);
      setEventCodes(client?.event_codes || []);
      setFormData(prev => ({ ...prev, events: {} }));
    }
  }, [formData.client_id, clients]);

  useEffect(() => {
    calculateTotals();
  }, [formData.events, eventCodes]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const driverResponse = await getDriverProfile();
      setDriver(driverResponse.data);

      // Charger les détails complets de chaque client (avec codes événements)
      const clientsWithDetails = await Promise.all(
        (driverResponse.data.clients || []).map(async (client) => {
          try {
            const clientDetails = await getClient(client.id);
            return { ...client, event_codes: clientDetails.data.event_codes };
          } catch (err) {
            console.error(`Erreur chargement client ${client.id}:`, err);
            return { ...client, event_codes: [] };
          }
        })
      );

      setClients(clientsWithDetails);

      const defaultClient = clientsWithDetails.find(c => c.is_default === 1);
      if (defaultClient) {
        setFormData(prev => ({ ...prev, client_id: defaultClient.id }));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de chargement des données. Veuillez rafraîchir la page.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let totalLoad = 0;
    let totalProcessed = 0;
    let totalRevenue = 0;

    eventCodes.forEach(eventCode => {
      const quantity = parseInt(formData.events[eventCode.id] || 0);
      const rate = parseFloat(eventCode.rate);

      if (quantity > 0) {
        if (eventCode.counted) {
          totalProcessed += quantity;
          if (eventCode.category === 'delivered' || eventCode.category === 'pickup') {
            totalLoad += quantity;
          }
        }
        totalRevenue += quantity * rate;
      }
    });

    setCalculated({
      total_load: totalLoad,
      total_processed: totalProcessed,
      total_revenue: totalRevenue
    });
  };

  const handleEventChange = (eventCodeId, value) => {
    setFormData(prev => ({
      ...prev,
      events: {
        ...prev.events,
        [eventCodeId]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_id) {
      alert('Veuillez sélectionner un client.');
      return;
    }

    const events = Object.entries(formData.events)
      .filter(([_, quantity]) => quantity && parseInt(quantity) > 0)
      .map(([eventCodeId, quantity]) => ({
        event_code_id: parseInt(eventCodeId),
        quantity: parseInt(quantity)
      }));

    if (events.length === 0) {
      alert('Veuillez saisir au moins une quantité.');
      return;
    }

    const dataToSend = {
      driver_id: driver.id,
      client_id: parseInt(formData.client_id),
      date: formData.date,
      events,
      comments: formData.comments
    };

    try {
      const response = await createDailyEntry(dataToSend);
      alert(`Saisie enregistrée avec succès !\n\nRésumé:\n• Emport: ${response.data.totals.total_load}\n• Total traité: ${response.data.totals.total_processed}\n• CA: ${response.data.totals.total_revenue.toFixed(2)} €`);

      setFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        client_id: clients.find(c => c.is_default)?.id || '',
        events: {},
        comments: ''
      });
    } catch (error) {
      alert('❌ Erreur: ' + (error.response?.data?.error || 'Une erreur est survenue'));
    }
  };

  if (loading) {
    return <div className="container"><div className="loading">Chargement...</div></div>;
  }

  const categoryLabels = {
    delivered: 'Livrés',
    pickup: 'Enlèvements',
    return: 'Retours'
  };

  const groupedEvents = eventCodes.reduce((acc, event) => {
    if (!acc[event.category]) acc[event.category] = [];
    acc[event.category].push(event);
    return acc;
  }, {});

  return (
    <div className="container">
      <div className="card">
        <h2>📝 Saisie Quotidienne</h2>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Saisissez vos quantités du jour. Le CA se calcule automatiquement selon les tarifs configurés.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Date et Client */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem', padding: '1.5rem', background: '#f0f8ff', borderRadius: '8px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>📅 Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                style={{ fontSize: '1.1rem', padding: '0.75rem' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>🏢 Client servi *</label>
              <select
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                required
                style={{ fontSize: '1.1rem', padding: '0.75rem' }}
              >
                <option value="">-- Sélectionner un client --</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name} ({client.payment_mode === 'stop' ? 'Au stop' : 'Au colis'})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedClient && (
            <>
              {/* Aide contextuelle */}
              <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '8px', marginBottom: '2rem', border: '1px solid #B9831C' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>
                  Mode de saisie : {selectedClient.payment_mode === 'stop' ? 'AU STOP' : 'AU COLIS'}
                </h4>
                <p style={{ margin: 0, fontSize: '0.95rem', color: '#856404' }}>
                  {selectedClient.payment_mode === 'stop'
                    ? 'Saisissez le nombre de STOPS (arrêts) pour chaque catégorie.'
                    : 'Saisissez le nombre de COLIS pour chaque catégorie.'}
                </p>
              </div>

              {/* Saisie des événements en GRID */}
              <div style={{ background: '#f8f9fa', padding: '2rem', borderRadius: '8px', marginBottom: '2rem' }}>
                {Object.entries(groupedEvents).map(([category, events]) => (
                  <div key={category} style={{ marginBottom: '2.5rem' }}>
                    <h3 style={{ marginBottom: '1.5rem', color: '#2c3e50', borderBottom: '3px solid #B8472E', paddingBottom: '0.5rem', fontSize: '1.3rem' }}>
                      {categoryLabels[category]}
                    </h3>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '1.5rem'
                    }}>
                      {events.map(event => (
                        <div
                          key={event.id}
                          style={{
                            background: 'white',
                            padding: '1.5rem',
                            borderRadius: '8px',
                            border: '3px solid #dee2e6',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.3s'
                          }}
                        >
                          <label style={{
                            display: 'block',
                            marginBottom: '1rem',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            color: '#2c3e50',
                            textAlign: 'center'
                          }}>
                            {event.label}
                          </label>

                          <input
                            type="number"
                            min="0"
                            value={formData.events[event.id] || ''}
                            onChange={(e) => handleEventChange(event.id, e.target.value)}
                            style={{
                              padding: '1rem',
                              fontSize: '1.5rem',
                              width: '100%',
                              fontWeight: 'bold',
                              border: '3px solid #B8472E',
                              borderRadius: '8px',
                              textAlign: 'center'
                            }}
                            placeholder="0"
                          />

                          {formData.events[event.id] > 0 && (
                            <div style={{
                              marginTop: '1rem',
                              padding: '0.75rem',
                              background: '#e8f5e9',
                              borderRadius: '6px',
                              textAlign: 'center',
                              border: '2px solid #27ae60'
                            }}>
                              <div style={{ fontSize: '0.85rem', color: '#27ae60', marginBottom: '0.25rem' }}>
                                Sous-total
                              </div>
                              <strong style={{ color: '#27ae60', fontSize: '1.2rem' }}>
                                {((formData.events[event.id] || 0) * event.rate).toFixed(2)} €
                              </strong>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Commentaires */}
              <div className="form-group">
                <label style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>💬 Commentaires (optionnel)</label>
                <textarea
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  rows="3"
                  placeholder="Informations complémentaires, incidents, remarques..."
                  style={{ fontSize: '1rem' }}
                />
              </div>

              {/* Récapitulatif */}
              <div style={{
                background: 'linear-gradient(135deg, #B8472E 0%, #6E2A19 100%)',
                padding: '2.5rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                color: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
              }}>
                <h3 style={{ marginBottom: '2rem', textAlign: 'center', fontSize: '1.8rem', fontWeight: 'bold' }}>
                  📊 Récapitulatif Automatique
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem', textAlign: 'center' }}>
                  <div style={{ background: 'rgba(255,255,255,0.15)', padding: '2rem', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.3)' }}>
                    <div style={{ fontSize: '1rem', marginBottom: '0.75rem', opacity: 0.9, fontWeight: 'bold' }}>EMPORT</div>
                    <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{calculated.total_load}</div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                      {selectedClient.payment_mode === 'stop' ? 'stops' : 'colis'}
                    </div>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.15)', padding: '2rem', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.3)' }}>
                    <div style={{ fontSize: '1rem', marginBottom: '0.75rem', opacity: 0.9, fontWeight: 'bold' }}>TOTAL TRAITÉ</div>
                    <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{calculated.total_processed}</div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                      avec retours
                    </div>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.15)', padding: '2rem', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.3)' }}>
                    <div style={{ fontSize: '1rem', marginBottom: '0.75rem', opacity: 0.9, fontWeight: 'bold' }}>CA DU JOUR</div>
                    <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{calculated.total_revenue.toFixed(2)} €</div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                      calculé auto
                    </div>
                  </div>
                </div>
              </div>

              {/* Bouton d'envoi */}
              <button
                type="submit"
                className="btn-success"
                style={{
                  width: '100%',
                  padding: '1.5rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  textTransform: 'uppercase'
                }}
              >
                Enregistrer la saisie du jour
              </button>
            </>
          )}

          {!selectedClient && (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#999' }}>
              <p style={{ fontSize: '1.5rem' }}>👆 Sélectionnez un client pour commencer la saisie</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default DailyEntryForm;
